/**
 * Time ports.
 *
 * The engine never counts ticks. `docs/02-ARCHITECTURE.md` §4.1 lists four
 * independent reasons a browser tick loop loses time — background throttling,
 * coalescing, process suspension, wall-clock mutation — and a decrementing loop
 * is wrong under every one of them. So the two roles are split into two ports
 * that cannot be confused for each other: {@link ClockPort} is the only source
 * of truth, and {@link TickerPort} is a cadence that carries no duration at all.
 */

import type { Milliseconds } from "../time";

/**
 * Undoes a subscription or a scheduled callback.
 *
 * One name for both because they carry the same rule: calling it more than once
 * is defined and harmless. A cancel that throws on the second call turns every
 * teardown path into a bookkeeping problem for the caller — React effects,
 * lifecycle handlers and error paths all legitimately cancel twice.
 */
export type Cancel = () => void;

declare const epochMillisecondsBrand: unique symbol;

/**
 * A wall-clock reading: whole milliseconds since the Unix epoch.
 *
 * Branded because the failure this port exists to prevent is arithmetic that
 * mixes a *reading* with a *duration*. `Milliseconds` is a duration; subtracting
 * two `EpochMilliseconds` yields one, and adding a duration to a reading yields
 * a reading. An unbranded alias would let `remaining - Date.now()` typecheck.
 *
 * The canonical persisted timestamp is `EffortInstant` (epoch **microseconds**,
 * `effort.ts`); convert with `effortInstantFromMilliseconds` at the boundary
 * rather than storing this type. This is the live reading, not the record.
 */
export type EpochMilliseconds = number & { readonly [epochMillisecondsBrand]: true };

export const EPOCH_MILLISECONDS_RANGE_ERROR =
  "Wall-clock reading is not a finite epoch-millisecond value";

/**
 * Rounds to whole milliseconds, because every duration in the product is a whole
 * millisecond and a fractional reading would leak a fraction into the first
 * subtraction. Throws rather than clamping on a non-finite input: a `NaN` clock
 * reading silently poisons every duration derived from it, and the throw names
 * the real fault at the point it happened.
 */
export function epochMilliseconds(value: number): EpochMilliseconds {
  if (!Number.isFinite(value)) {
    throw new RangeError(EPOCH_MILLISECONDS_RANGE_ERROR);
  }
  return Math.round(value) as EpochMilliseconds;
}

/**
 * Both clocks read together.
 *
 * Reconciliation (§4.4) needs a monotonic delta *and* a wall delta describing
 * the same moment. Reading the two clocks through separate calls admits skew
 * between them — under load the gap is real — and that skew lands directly in
 * the "did the device sleep or did the clock jump" comparison. One call, one
 * moment.
 */
export interface ClockReading {
  /** Monotonic timeline position. Only its *differences* mean anything. */
  readonly monotonic: Milliseconds;
  readonly wall: EpochMilliseconds;
}

export interface ClockPort {
  /**
   * The wall clock. Timestamps and wall duration only — never active duration.
   * It is allowed to jump forwards or backwards (NTP, manual change, DST) and
   * callers must treat a backwards jump as an anomaly to record, not propagate.
   */
  now(): EpochMilliseconds;

  /**
   * The monotonic timeline, unaffected by wall-clock changes. **The only source
   * of active duration.** Implementations return whole milliseconds so that
   * engine arithmetic stays integral; `performance.now()` is fractional and must
   * be floored by the adapter, not by every caller.
   */
  monotonic(): Milliseconds;

  /** Both clocks from one pass. See {@link ClockReading}. */
  read(): ClockReading;

  /**
   * The wall instant at which {@link monotonic} read zero — `performance.timeOrigin`
   * on the web.
   *
   * It exists because a document reload always breaks the monotonic timeline
   * (§4.5): the origin persisted with an open-effort checkpoint will not match
   * the origin of the document that resumes it. Comparing them is how the engine
   * knows it has no monotonic evidence across the gap, so the gap extends **wall**
   * duration only. Without this reading the engine cannot tell a resumed document
   * from a continuing one, and would either invent active time or erase it.
   */
  monotonicOrigin(): EpochMilliseconds;

  /** The device's current IANA time-zone identifier, e.g. `"Europe/London"`. */
  timeZone(): string;

  /**
   * The UTC offset in seconds that `timeZone` was observing at `instant`.
   *
   * Returns `undefined` for an identifier the platform does not know, which is
   * the only honest answer and maps directly onto `ManualEffortValidationError`'s
   * `invalidTimeZoneIdentifier`. Manual retrospective entry needs offsets at
   * specific instants — not a single current offset — because resolving an
   * entered local time across a DST boundary is exactly where a spring-forward
   * gap and a fall-back overlap have to become first-class states rather than a
   * silent one-hour rewrite of what the user said they did.
   */
  resolvedOffsetSeconds(instant: EpochMilliseconds, timeZone: string): number | undefined;
}

/**
 * The display cadence, and nothing else.
 *
 * §5 of the parity spec keeps the native 0.25 s tick — but as a *repaint prompt*.
 * The callback receives no arguments by design: if it carried an elapsed value
 * some caller would eventually subtract it, and the entire reason this port is
 * separate from {@link ClockPort} would be lost.
 */
export interface TickerPort {
  /** Repeats until cancelled. */
  every(interval: Milliseconds, callback: () => void): Cancel;
  /** Fires once, unless cancelled first. */
  after(delay: Milliseconds, callback: () => void): Cancel;
}

/**
 * The cadence the round clock repaints at. A display value: nothing derives
 * duration from it, and a tick that arrives late or not at all changes only how
 * soon the screen catches up, never what the engine believes happened.
 */
export const DISPLAY_TICK_INTERVAL: Milliseconds = 250;
